import {
  Button,
  Checkbox,
  darken,
  FormControlLabel,
  MenuItem,
  Paper,
  SelectChangeEvent,
  Theme,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Formik } from 'formik';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  useRecoilState,
  useRecoilStateLoadable,
  useRecoilValueLoadable,
} from 'recoil';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import { FormikSelect, FormikSwitch } from '../../../common/formik-material-ui';
import {
  IBusinessUser,
  IUserAtom,
  Role,
  user,
} from '../../../recoil/atoms/user';
import { methodNameEnum } from '../../../recoil/selectors/makePHPRequest';
import {
  IPermissions,
  permissionsSelector,
} from '../../../recoil/selectors/permissionsSelector';
import { makeApiRequestCached } from '../../../services/rest/makeAPIRequest';
import { POWERDIALER_MACHINE_DETECT_PRODUCT_ID } from '../../pages/ManageAccount';
import { InfoTooltip } from './ManageAccountModal';
import { ModalWithStyles } from './ModalWithStyles';

import {
  useBulkChangeProductsMutation,
  useGetActiveUserProductsQuery,
  useUpdateUserProductSettingsMutation,
} from '../../../api/apigQuery';
import { currentTeam } from '../../../recoil/atoms/currentTeam';
import { TeamDropdown } from '../dropdowns/TeamDropdown';

import { GridRowData, GridSortModel } from '@mui/x-data-grid';
import { Method } from 'axios';
import get from 'lodash/get';
import { useMutation } from 'react-query';
import { useMakePHPRequest } from '../../../common/useMakePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  DataTable,
  IColDef,
} from '../../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import {
  getBillingTiers,
  getPowerlistDialerProductMap,
  products,
} from '../products';
import { ConfirmAgentPowerlists } from './ConfirmAgentPowerlists';

const useStyles = makeStyles((theme: Theme) => ({
  dealButton: {
    backgroundColor: 'rgb(98,93,236)',
    '&:hover': {
      backgroundColor: darken(
        'rgb(98,93,236)',
        theme.palette.mode === 'light' ? 0.2 : 0
      ),
    },
    '&:focus': {
      backgroundColor: darken(
        'rgb(98,93,236)',
        theme.palette.mode === 'light' ? 0.2 : 0
      ),
    },
  },
  field: {
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : 'none',
  },
  search: {
    marginTop: '5px', // zero out material-ui dense
    marginBottom: 0, // zero out material-ui dense
    marginRight: '1vw', // space for loading icon
  },
  manageProductModalContainer: {
    backgroundColor: theme.palette.mode === 'dark' ? '#003D50' : '#e5f4f9',
    '& .MuiFormLabel-root': {
      background: theme.palette.mode === 'dark' ? 'none' : '',
    },
  },
  fullWidth: {
    width: '100% !important',
  },
}));

const ManageAgentProductsModal = ({
  endpointEnum,
  show,
  handleClose,
  formikValues,
}) => {
  const classes = useStyles();
  const [team, setTeam] = useRecoilState(currentTeam);
  const [bulkProductMutation] = useBulkChangeProductsMutation();
  const [
    updateUserProductSettingsMutation,
  ] = useUpdateUserProductSettingsMutation();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const userAtomContents = userAtomLoadable.contents as IUserAtom;
  const [allSelected, setAllSelected] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [searchFiltered, setSearchFiltered] = useState<GridRowData[]>([]);
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showDowngrade, setShowDowngrade] = useState(false);
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  const powerlistDialerProductMap = getPowerlistDialerProductMap(
    POWERDIALER_MACHINE_DETECT_PRODUCT_ID
  );
  const [loading, setLoading] = useState(false);
  React.useEffect(() => {
    setTeam({ id: 0, name: 'All Teams' });
    refetchToggleMyBusinessUsers({});
  }, []);
  const makeRequest = useMakePHPRequest({
    userAtom: userAtomContents,
  });
  const [
    _myBusinessUsersCached,
    myBusinessUsersLoadable,
    refetchToggleMyBusinessUsers,
  ] = makeApiRequestCached<IBusinessUser | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.filter(
        (user) => user.isactive === 1
      ) ?? cached?.current
  );
  const {
    role,
    canAccessPowerlistProduct,
    isBusinessEligibleToAccessCallTranscriptions,
    isMachineDetectionFeatureEnabled,
  } = useRecoilValueLoadable<IPermissions>(permissionsSelector)
    .contents as IPermissions;
  const isManagerBoolean = role === Role.MANAGER;
  const isAdminBoolean = role === Role.ADMIN;

  const hasCallTranscriptionFeaturedEnabled =
    process.env.VITE_APP_FEATURE_CALL_TRANSCRIPTIONS === '1' &&
    isBusinessEligibleToAccessCallTranscriptions === true;
  const rowsCached = useRef<any[]>([]);
  rowsCached.current = React.useMemo(() => {
    let users;
    if (myBusinessUsersLoadable?.state !== 'loading') {
      users = myBusinessUsersLoadable.contents?.response?.data?.result
        ?.filter((user) => user.isactive === 1)
        .map((row: any) => ({
          checked: selected.indexOf(+row.userid) !== -1,
          id: +row.userid, // allow number sorting
          agentName:
            row?.fname || row?.lname
              ? `${row?.fname} ${row?.lname}`
              : row.userid,
          agentType: row?.type ?? '',
          teams: row.teams,
        }));
    } else {
      users = rowsCached.current;
    }
    if (team?.id > 0) {
      const filtered = users.filter((agent, i) => {
        if (!agent.teams || agent.teams === null) {
          return false;
        }
        const agentTeams = agent?.teams?.split(',');
        return agentTeams?.indexOf(String(team?.id)) !== -1;
      });
      users = filtered;
    }
    return users;
  }, [myBusinessUsersLoadable, team, selected, allSelected]);

  const [sortModel, _setSortModel] = React.useState<GridSortModel>([
    { field: 'id', sort: 'asc' },
  ]);

  React.useEffect(() => {
    if (
      allSelected &&
      rowsCached?.current?.length > 0 &&
      selected.length !== rowsCached?.current?.length
    ) {
      const selectedToUpdate = [...(rowsCached?.current ?? [])]?.map(
        (el) => el?.id
      );
      setSelected(selectedToUpdate);
    }
    if (
      allSelected === false &&
      rowsCached?.current?.length > 0 &&
      selected.length === rowsCached?.current?.length &&
      team?.id === 0
    ) {
      setSelected([]);
    }
  }, [allSelected, selected]);

  const columns: IColDef[] = useMemo(
    () => [
      {
        field: 'checked',
        enableSorting: false,
        Header: () => {
          return (
            <Checkbox
              onChange={(event) => {
                if (searchFiltered?.length) {
                  const selectedToUpdate = [...selected];
                  if (event?.target?.checked) {
                    searchFiltered?.forEach((row) => {
                      if (selectedToUpdate.indexOf(row.id) === -1) {
                        selectedToUpdate.push(row.id);
                      }
                    });
                  } else {
                    searchFiltered?.forEach((row) => {
                      if (selectedToUpdate.indexOf(row.id) !== -1) {
                        selectedToUpdate.splice(
                          selectedToUpdate.indexOf(row.id),
                          1
                        );
                      }
                    });
                  }
                  setSelected(selectedToUpdate);
                } else if (team?.id === 0) {
                  setAllSelected(event?.target?.checked);
                } else {
                  const currentTeam = rowsCached.current;
                  const selectedToUpdate = [...selected];

                  if (event?.target?.checked) {
                    currentTeam?.forEach((row) => {
                      if (selectedToUpdate.indexOf(row?.id) === -1) {
                        selectedToUpdate.push(row?.id);
                      }
                    });
                  } else {
                    currentTeam?.forEach((row) => {
                      if (selectedToUpdate.indexOf(row?.id) !== -1) {
                        selectedToUpdate.splice(
                          selectedToUpdate.indexOf(row?.id),
                          1
                        );
                      }
                    });
                  }

                  setSelected(selectedToUpdate);
                }
              }}
              color="primary"
              checked={
                Boolean(team?.id === 0 && allSelected) ||
                Boolean(rowsCached?.current?.every((el) => el?.checked)) ||
                Boolean(
                  searchFiltered?.length &&
                    searchFiltered?.every((el: any) => el?.checked)
                )
              }
            />
          );
        },
        renderCell: (params) => {
          return (
            <Checkbox
              color="primary"
              // indeterminate={partiallySelected}
              onChange={(event) => {
                if (event?.target?.checked) {
                  setSelected([...selected, +params.row.id]);
                } else {
                  const foundIdx = selected.indexOf(+params.row.id);
                  if (event?.target?.checked === false && foundIdx !== -1) {
                    const selectedToUpdate = [...selected];
                    selectedToUpdate.splice(foundIdx, 1);
                    setSelected(selectedToUpdate);
                  }
                  if (event?.target?.checked === false && foundIdx > -1) {
                    const selectedToUpdate = [...selected];
                    selectedToUpdate.splice(foundIdx, 1);
                    setSelected(selectedToUpdate);
                    setAllSelected(false);
                  }
                }
              }}
              checked={params.value === true || allSelected}
            />
          );
        },
      },
      {
        field: 'id',
        headerName: 'ID',
        headerClassName: 'superStyle',
        hide: isManagerBoolean || isAdminBoolean,
        width: 80,
      },
      {
        field: 'agentName',
        headerName: 'Agent Name',
        // width: 130,
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
      },
      {
        field: 'agentType',
        headerName: 'Agent Type',
        // width: 130,
        headerClassName: classes.fullWidth,
        cellClassName: classes.fullWidth,
      },
    ],
    [
      myBusinessUsersLoadable,
      allSelected,
      selected,
      team,
      rowsCached.current,
      searchFiltered,
      isAdminBoolean,
      isManagerBoolean,
    ]
  );

  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        {...props}
        dataType="Dispositions"
        numSelected={selected.length}
        csvData={false}
        leftComponent={
          <div style={{ maxWidth: '175px' }}>
            <TeamDropdown />
          </div>
        }
      />
    ),
    [selected.length]
  );
  const initialValues = useMemo(() => {
    return {
      billingTier: formikValues?.accountSettings?.billingTier ?? 0,
      minutesPlan: formikValues?.accountSettings?.minutesPlan ?? 0,
      powerlistProduct: formikValues?.manage?.powerlistProduct ?? 0,
      connectionBoost: formikValues?.accountSettings?.localP ?? false,
      ftcBlocking: formikValues?.accountSettings?.ftcBlocking ?? false,
      hasPdCallTranscription:
        formikValues?.manage?.hasPdCallTranscription ?? false,
      agents: [],
    };
  }, [formikValues]);

  const {
    data: activeUserProducts,
    isFetching: getActiveUserProductsLoading,
    refetch: refetchGetActiveUserProducts,
  } = useGetActiveUserProductsQuery(
    {
      userSettings: {
        businessId: userAtomContents?.businessid,
        userid: userAtomContents?.businessid,
        apikey: userAtomContents?.apikey,
      },
      userIds: selected,
    },
    { skip: !userAtomContents?.businessid }
  );

  return (
    <>
      <Formik
        onSubmit={async (values, actions) => {
          const {
            minutesPlan,
            billingTier,
            powerlistProduct,
            connectionBoost,
            hasPdCallTranscription,
            ftcBlocking,
          } = values as any;
          // handle billingTier and Minutes plan updates
          const billingTiers = getBillingTiers(Role, role);
          let tierName = '';
          if (minutesPlan === 1) {
            if (billingTier === 1) {
              tierName = 'Enterprise Unlimited';
            } else if (billingTier === 2) {
              tierName = 'Integrated Unlimited';
            } else if (billingTier === 3) {
              tierName = 'Basic Unlimited';
            }
          } else if (minutesPlan === 0) {
            if (billingTier === 1) {
              tierName = 'Enterprise';
            } else if (billingTier === 2) {
              tierName = 'Integrated';
            } else if (billingTier === 3) {
              tierName = 'Basic';
            }
          }
          if (billingTier === 4) {
            tierName = 'payperuse';
          }
          const productName = products.find(
            (product) => product?.tier === tierName
          )?.product;
          const productTier = products.find(
            (product) => product?.tier === tierName
          )?.tier;
          let bUpdate = [];
          let count = 0;
          const updateUserProducts = async (userProductsToUpdate) => {
            try {
              const res: any = await bulkProductMutation({
                userSettings: {
                  apikey: userAtomContents?.apikey,
                  businessId: userAtomContents?.businessid,
                  userid: userAtomContents?.userid,
                },
                content: userProductsToUpdate,
                preflight: 0,
              });
              if (res?.data?.content?.length) {
                return true;
              } else {
                return false;
              }
            } catch (err) {
              return false;
            }
          };
          const updateUserProductSettings = async () => {
            try {
              const res: any = await updateUserProductSettingsMutation({
                userSettings: {
                  apikey: userAtomContents?.apikey,
                  businessId: userAtomContents?.businessid,
                  userid: userAtomContents?.userid,
                },
                userIds: selected,
                ftcBlocking: ftcBlocking ? 1 : 0,
                localPresenceLite: connectionBoost ? 1 : 0,
                userTier: productTier!,
              });
              if (res?.data?.success) {
                return true;
              }
            } catch (error) {
              return false;
            }
          };
          const updateAllSettings = async () => {
            selected.forEach(async (id, j) => {
              const currentUser = (myBusinessUsersLoadable?.contents?.response
                ?.data?.result as any)?.find((user) => user.userid == id);
              const businessId = userAtomContents.businessid?.toString() ?? '';
              const userId = currentUser?.userid?.toString() ?? '';
              let hasUserSettingsUpdated = false;
              // const updatePdCallTranscriptionProduct =
              //   hasPdCallTranscription !==
              //   Boolean(currentUser?.hasPdCallTranscription);
              if (
                ((currentUser?.powerlistEndDate &&
                !currentUser?.nextPowerlistProductId
                  ? 0
                  : currentUser?.nextPowerlistProductId) ??
                  currentUser?.powerlistProductId ??
                  0) !== powerlistProduct
              ) {
                // // placeholder for actual call
                // await mutation.mutateAsync({
                //   endpoint: endpointEnum.UPDATE_POWERLIST_PRODUCT,
                //   payload: {
                //     call: 'updatePowerlistProduct',
                //     productValue: powerlistProduct === 0 ? 0 : 1,
                //     productid:
                //       powerlistProduct === 0
                //         ? currentUser?.powerlistProductId
                //         : powerlistProduct,
                //     userid: currentUser?.userid,
                //   },
                // });
                hasUserSettingsUpdated = true;
              }
              // if (
              //   hasCallTranscriptionFeaturedEnabled &&
              //   businessId &&
              //   userId &&
              //   updatePdCallTranscriptionProduct
              // ) {
              //   await mutation.mutateAsync({
              //     method: 'PUT' as Method,
              //     domain: process.env.VITE_APP_CDK_DOMAIN,
              //     stage: 'auto',
              //     endpoint: endpointEnum.CALL_TRANSCRIPTION_PRODUCT.replace(
              //       '{businessId}',
              //       businessId
              //     ).replace('{userId}', userId),
              //     payload: {
              //       hasProduct: Number(hasPdCallTranscription),
              //     },
              //   });
              // }
              if (hasUserSettingsUpdated && businessId && userId) {
                await mutation.mutateAsync({
                  method: 'POST' as Method,
                  domain: process.env.VITE_APP_CDK_DOMAIN,
                  stage: 'auto',
                  endpoint: endpointEnum.USER_SETTINGS_UPDATED.replace(
                    '{businessId}',
                    businessId
                  ).replace('{userId}', userId),
                });
              }
              if (j + 1 === selected.length) {
                setLoading(false);
                refetchToggleMyBusinessUsers({});
                refetchGetActiveUserProducts();
                enqueueSnackbarWithStyles({
                  message: 'Successfully Updated Agent Products',
                  alertProps: { severity: 'success' },
                });
                handleClose();
              }
            });
          };

          setLoading(true);
          selected.forEach(async (id, i) => {
            let userProductsToUpdate: {
              userId: number;
              product: string;
            }[] = [];
            const currentUser = (myBusinessUsersLoadable?.contents?.response
              ?.data?.result as any)?.find((user) => user.userid == id);
            const currentBillingTier =
              billingTiers[
                currentUser?.nextUserTier || (currentUser?.userTier as string)
              ]?.tier ?? '';

            const currentMinutesPlan =
              billingTiers[
                currentUser?.nextUserTier || (currentUser?.userTier as string)
              ]?.unlimited ?? '';
            // value changes compared against current user
            const billingTierChange = currentBillingTier !== billingTier;
            const minutesPlanChange = currentMinutesPlan !== minutesPlan;
            const currentPowerlistProduct =
              currentUser?.nextPowerlistProductId ||
              (currentUser?.powerlistEndDate
                ? 0
                : currentUser?.powerlistProductId || 0);

            const powerlistProductChange =
              currentPowerlistProduct !== powerlistProduct;
            const connectionBoostChange =
              connectionBoost !== (currentUser?.localPresenceLite === 1);
            const DNCAlgorithmChange =
              ftcBlocking !== (currentUser?.ftcBlocking === 1);
            const updatePdCallTranscriptionProduct =
              hasPdCallTranscription !==
              Boolean(currentUser?.hasPdCallTranscription);
            const currentUserProducts = activeUserProducts?.result?.filter(
              (userProducts) => userProducts?.userid === id
            );
            const activeUserProductsToUpdate = currentUserProducts?.length
              ? [...currentUserProducts]
              : [];

            if (billingTierChange || minutesPlanChange) {
              userProductsToUpdate = [
                ...userProductsToUpdate,
                {
                  userId: id,
                  product: productName,
                  productFamily: 'phone',
                },
              ] as any;
            }
            // billing tier / minutes plan changes
            const data = {
              tierprocess: 'change',
              extension: currentUser?.userEXT ?? '',
              usertype: currentUser?.type,
              tier: billingTier,
              unlimited: minutesPlan,
              userid: id,
              useBusinessHoursEnabled: !!get(
                currentUser,
                `useBusinessHoursEnabled`,
                true
              )
                ? '1'
                : '0',
            };
            await makeRequest({
              methodName: methodNameEnum.UPDATE_MANAGEMENT_OPTIONS,
              payload: data,
            });
            // handle powerlist product updates

            if (powerlistProductChange) {
              const selectedPowerlistProduct = powerlistDialerProductMap?.find(
                (el) => el?.id === powerlistProduct
              );

              // if the user has selected none for powerlistProduct
              if (selectedPowerlistProduct?.id === 0) {
                // if the userProducts has a power list product remove it
                activeUserProductsToUpdate?.forEach((product, i) => {
                  if (product?.productFamily === 'powerlistDialer') {
                    activeUserProductsToUpdate.splice(i, 1);
                  }
                });
              } else {
                activeUserProductsToUpdate?.forEach((product, i) => {
                  if (product?.productFamily === 'powerlistDialer') {
                    activeUserProductsToUpdate.splice(i, 1);
                  }
                });
                userProductsToUpdate = [
                  ...userProductsToUpdate,
                  {
                    userId: id,
                    product: selectedPowerlistProduct?.product,
                    productFamily: 'powerlistDialer',
                  },
                ] as any;
              }
            }
            // handle connectionBoost changes

            if (connectionBoostChange) {
              if (connectionBoost) {
                userProductsToUpdate = [
                  ...userProductsToUpdate,
                  {
                    userId: id,
                    product: 'localpresencelite',
                    productFamily: 'localPresenceLite',
                  },
                ] as any;
              } else {
                // remove connectionBoostChange
                let indexOfLocalP = -1;
                activeUserProductsToUpdate?.forEach((product, i) => {
                  if (product?.productFamily === 'localPresenceLite') {
                    indexOfLocalP = i;
                  }
                });
                if (indexOfLocalP !== -1) {
                  activeUserProductsToUpdate.splice(indexOfLocalP, 1);
                }
              }
            }
            // handle DNC changes

            if (DNCAlgorithmChange) {
              if (ftcBlocking) {
                userProductsToUpdate = [
                  ...userProductsToUpdate,
                  {
                    userId: id,
                    product: 'ftcblocking',
                    productFamily: 'ftcBlocking',
                  },
                ] as any;
              } else {
                // remove ftcBlocking
                let indexOfFtcBlocking = -1;
                activeUserProductsToUpdate?.forEach((product, i) => {
                  if (product?.productFamily === 'ftcBlocking') {
                    indexOfFtcBlocking = i;
                  }
                });
                if (indexOfFtcBlocking !== -1) {
                  activeUserProductsToUpdate.splice(indexOfFtcBlocking, 1);
                }
              }
            }
            // handle CI changes
            if (updatePdCallTranscriptionProduct) {
              if (hasPdCallTranscription) {
                userProductsToUpdate = [
                  ...userProductsToUpdate,
                  {
                    userId: id,
                    product: 'calltranscription',
                    productFamily: 'conversationIntelligence',
                  },
                ] as any;
              } else {
                // remove calltranscription
                let indexOfCalltranscription = -1;
                activeUserProductsToUpdate?.forEach((product, i) => {
                  if (product?.productFamily === 'conversationIntelligence') {
                    indexOfCalltranscription = i;
                  }
                });
                if (indexOfCalltranscription !== -1) {
                  activeUserProductsToUpdate.splice(
                    indexOfCalltranscription,
                    1
                  );
                }
              }
            }
            const totalProductsToUpdate = [...userProductsToUpdate];
            const updatedFamilies = userProductsToUpdate?.map(
              (product) => (product as any)?.productFamily
            );
            activeUserProductsToUpdate?.forEach((product, i) => {
              if (updatedFamilies?.indexOf(product?.productFamily) === -1) {
                totalProductsToUpdate.push({
                  userId: product?.userid,
                  product: product?.product,
                });
              }
            });
            const updated = totalProductsToUpdate.map((product) => ({
              userId: product.userId,
              product: product?.product,
            }));
            const current = [...bUpdate];
            bUpdate = (current as any)?.concat(updated);
            if (current?.length + updated?.length === bUpdate?.length) {
              count = count + 1;
            }
            if (selected?.length === count) {
              const successBulkUpdate = await updateUserProducts(bUpdate);
              const successSettingsUpdate = await updateUserProductSettings();
              if (successBulkUpdate && successSettingsUpdate) {
                await updateAllSettings();
              } else {
                setLoading(false);
                refetchToggleMyBusinessUsers({});
                enqueueSnackbarWithStyles({
                  message: `Agent product changes not saved.`,
                  alertProps: { severity: 'error' },
                });
                handleClose();
              }
            }
          });
        }}
        enableReinitialize={true}
        initialValues={initialValues}
      >
        {(formik) => (
          <>
            <ModalWithStyles
              title="Edit Agents"
              aria-labelledby="example-modal-sizes-title-md"
              show={show}
              onHide={handleClose}
              onSubmit={formik.handleSubmit}
              size="sm"
              deleteDialog={false}
              isLoading={
                myBusinessUsersLoadable?.state === 'loading' ||
                getActiveUserProductsLoading ||
                loading
              }
              footerComponent={
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      width: '100%',
                      justifyContent: 'flex-end',
                      gap: '12px',
                    }}
                  >
                    <Button
                      variant="contained"
                      disableElevation={true}
                      onClick={() => {
                        handleClose();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      color="primary"
                      className={classes.dealButton}
                      variant="contained"
                      disableElevation={true}
                      disabled={
                        selected?.length === 0 || getActiveUserProductsLoading
                      }
                      onClick={(event: any) => formik.handleSubmit(event)}
                    >
                      Apply to ({selected?.length}) Agents
                    </Button>
                  </div>
                </div>
              }
            >
              <form onSubmit={formik.handleSubmit}>
                <LabelledOutline
                  label="Agent Settings"
                  className={classes.manageProductModalContainer}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-around',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '50%',
                      }}
                    >
                      <div>
                        <FormikSelect
                          name="billingTier"
                          className={classes.field}
                          formik={formik}
                          fullWidth
                          label="Seat Type"
                          size="small"
                          variant="outlined"
                          required
                          onChange={(
                            event: SelectChangeEvent | React.ChangeEvent<any>
                          ) => {
                            if (
                              // if not enterprise
                              event.target.value != 1 &&
                              // if not none
                              formik.values.powerlistProduct !== 0
                            ) {
                              setShowDowngrade(true);
                            }
                            if (event.target.value !== 2) {
                              formik.setFieldValue('powerlistProduct', 0);
                              formik.setFieldValue('billingTier', 0);
                              formik.setFieldValue('connectionBoost', false);
                              formik.setFieldValue(
                                'hasPdCallTranscription',
                                false
                              );
                            }
                          }}
                        >
                          <MenuItem value={2}>Integrated</MenuItem>
                          <MenuItem value={1}>Professional User</MenuItem>
                        </FormikSelect>
                      </div>
                      <div>
                        <FormikSelect
                          name="minutesPlan"
                          className={classes.field}
                          formik={formik}
                          fullWidth
                          label="Minutes Plan"
                          size="small"
                          variant="outlined"
                          required
                          disabled={
                            formik.values.billingTier.toString() === '4'
                          }
                        >
                          <MenuItem value={1}>Unlimited</MenuItem>
                          <MenuItem value={0}>Per Minute</MenuItem>
                        </FormikSelect>
                      </div>
                      {canAccessPowerlistProduct && (
                        <div>
                          <FormikSelect
                            name="powerlistProduct"
                            className={classes.field}
                            formik={formik}
                            fullWidth
                            label="PowerDialer Product"
                            size="small"
                            variant="outlined"
                            required
                            data-pup="edit-agent-powerlist-product"
                            disabled={formik?.values?.billingTier !== 1}
                            onChange={(
                              event: SelectChangeEvent | React.ChangeEvent<any>
                            ) => {
                              // show billing tier upgrade to enterprise message
                              if (
                                // if not enterprise
                                formik.values.billingTier != 1 &&
                                // if not none
                                event.target.value !== 0
                              ) {
                                setShowUpgrade(true);
                              }
                            }}
                          >
                            <MenuItem
                              value={0}
                              data-pup="edit-agent-powerlist-none"
                            >
                              None
                            </MenuItem>
                            <MenuItem
                              value={30}
                              data-pup="edit-agent-powerlist-one"
                            >
                              PowerDialer One
                            </MenuItem>
                            <MenuItem
                              value={31}
                              data-pup="edit-agent-powerlist-four"
                            >
                              PowerDialer Four
                            </MenuItem>
                            <MenuItem
                              value={32}
                              data-pup="edit-agent-powerlist-ten"
                            >
                              PowerDialer Ten
                            </MenuItem>
                            {isMachineDetectionFeatureEnabled && (
                              <MenuItem
                                value={POWERDIALER_MACHINE_DETECT_PRODUCT_ID}
                                data-pup="edit-agent-powerlist-machine-detect"
                              >
                                <div style={{ display: 'flex', width: '100%' }}>
                                  <span style={{ flexGrow: 1 }}>
                                    PowerDialer AI Human Detect
                                  </span>
                                  <span>
                                    <InfoTooltip title="Enables PowerDialer to skip calls where a Voicemail or IVR phone tree is detected. This results in more calls connecting to actual leads." />
                                  </span>
                                </div>
                              </MenuItem>
                            )}
                          </FormikSelect>
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        // width: '50%',
                        // padding: '24px 24px 24px 12px',
                      }}
                    >
                      <div>
                        {role === Role.SUPERADMIN && (
                          <div
                            style={{ paddingLeft: '12px' }}
                            className={'superStyle'}
                          >
                            <FormControlLabel
                              control={
                                <FormikSwitch
                                  name="connectionBoost"
                                  formik={formik}
                                  color="primary"
                                  type="checkbox"
                                  disabled={
                                    formik?.values?.billingTier?.toString() !==
                                    '1'
                                  }
                                />
                              }
                              label="Connection Boost &nbsp;&nbsp;"
                            />
                          </div>
                        )}
                      </div>
                      <div>
                        {role === Role.SUPERADMIN &&
                          process.env.VITE_APP_FEATURE_FTC_BLOCKING === '1' && (
                            <div
                              style={{ paddingLeft: '12px' }}
                              className={'superStyle'}
                            >
                              <FormControlLabel
                                control={
                                  <FormikSwitch
                                    name="ftcBlocking"
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                  />
                                }
                                disabled={
                                  formik?.values?.billingTier?.toString() !==
                                  '1'
                                }
                                label="DNC Algorithm &nbsp;&nbsp;"
                              />
                            </div>
                          )}
                      </div>
                      <div>
                        {hasCallTranscriptionFeaturedEnabled &&
                          (role === Role.SUPERADMIN ||
                            role === Role.ADMIN ||
                            role === Role.MANAGER) && (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-start',
                                alignItems: 'center',
                                marginBottom: '8px',
                              }}
                            >
                              <FormControlLabel
                                style={{
                                  marginLeft: '0px',
                                }}
                                control={
                                  <FormikSwitch
                                    name="hasPdCallTranscription"
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                  />
                                }
                                disabled={
                                  formik?.values?.billingTier?.toString() !==
                                  '1'
                                }
                                label="Conversation Intelligence"
                              />
                              <div
                                style={{
                                  margin: '0px -12px 6px -8px',
                                  color: '#0098C7',
                                }}
                              >
                                <InfoTooltip
                                  {...{ fontSize: 'small' }}
                                  title="Enabling this option will transcribe and analyze calls for this agent. Note: This will add an additional $50 per month to the agent subscription."
                                />
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                </LabelledOutline>
                <Paper
                  elevation={0}
                  style={{ overflow: 'scroll', maxHeight: '450px' }}
                >
                  <DataTable
                    enableSelectAll={true}
                    rows={rowsCached?.current ?? []}
                    columns={columns}
                    loading={myBusinessUsersLoadable.state === 'loading'}
                    ToolbarComponent={ToolbarComponent}
                    sortModel={sortModel}
                    setFilteredRowsForBulkProducts={(e) => {
                      e?.length === rowsCached?.current?.length
                        ? setSearchFiltered([])
                        : setSearchFiltered(e);
                    }}
                  />
                </Paper>
              </form>
            </ModalWithStyles>
            <ConfirmAgentPowerlists
              title="Enable PowerDialer?"
              message={
                <>
                  <p>
                    Selecting a PowerDialer product will upgrade your billing
                    tier to Professional. Select Continue to proceed or Cancel
                    to go back.
                  </p>
                  <p>Do you want to continue?</p>
                </>
              }
              // handleClose={handleCloseDelete}
              handleClose={() => {
                // set powerdialer product back to none
                formik.setFieldValue('powerlistProduct', 0);
                setShowUpgrade(false);
              }}
              handleSubmit={() => {
                // set billing tier to enterprise
                formik.setFieldValue('billingTier', 1);
                // close confirmation
                setShowUpgrade(false);
              }}
              show={showUpgrade}
            />
            <ConfirmAgentPowerlists
              title="Disable PowerDialer?"
              message={
                <>
                  <p>
                    Downgrading your billing tier will remove the PowerDialer
                    product. Select Continue to proceed or Cancel to go back.
                  </p>
                  <p>Do you want to continue?</p>
                </>
              }
              // handleClose={handleCloseDelete}
              handleClose={() => {
                // set billing tier back to enterprise
                formik.setFieldValue('billingTier', 1);
                setShowDowngrade(false);
              }}
              handleSubmit={() => {
                // set powerdialer product back to none
                formik.setFieldValue('powerlistProduct', 0);
                // close confirmation
                setShowDowngrade(false);
              }}
              show={showDowngrade}
            />
          </>
        )}
      </Formik>
    </>
  );
};
export default ManageAgentProductsModal;
